import React from 'react';
import { ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { useLanguage, Language } from '@/contexts/LanguageContext';

const languages: { code: Language; name: string; flag: string }[] = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'ro', name: 'Română', flag: '🇷🇴' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
];

interface LanguageSelectorProps {
  variant?: 'header' | 'mobile' | 'menu';
  isDoorsPage?: boolean;
}

export default function LanguageSelector({ variant = 'header', isDoorsPage = false }: LanguageSelectorProps) {
  const { currentLanguage, setLanguage, t } = useLanguage();
  const currentLang = languages.find(lang => lang.code === currentLanguage) || languages[0];

  if (variant === 'menu') {
    return (
      <div className="flex items-center gap-4">
        {languages.map((language) => (
          <button
            key={language.code}
            onClick={() => {
              setLanguage(language.code);
            }}
            className={`text-4xl transition-all duration-300 focus:outline-none ${
              currentLanguage === language.code
                ? 'scale-125 drop-shadow-[0_0_20px_rgba(59,130,246,0.6)] grayscale-0 opacity-100'
                : 'grayscale opacity-70 hover:grayscale-0 hover:opacity-100 hover:scale-110'
            }`}
            aria-label={`Switch to ${language.name}`}
            type="button"
          >
            {language.flag}
          </button>
        ))}
      </div>
    );
  }

  if (variant === 'mobile') {
    return (
      <div className="flex flex-col items-center space-y-4">
        <span className="text-lg font-light tracking-wide text-muted-foreground">{t("ui.language")}</span>
        <div className="flex space-x-4">
          {languages.map((language) => (
            <button
              key={language.code}
              onClick={() => setLanguage(language.code)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 ${
                currentLanguage === language.code
                  ? 'bg-primary/20 text-primary border border-primary/30'
                  : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              <span className="text-lg">{language.flag}</span>
              <span className="text-sm font-medium">{language.name}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`h-8 px-2 text-sm font-normal gap-1 hover:bg-muted/50 ${
            isDoorsPage ? "text-white hover:text-white" : ""
          }`}
        >
          <span className="text-base">{currentLang.flag}</span>
          <span className={`hidden sm:inline-block ${isDoorsPage ? "text-white" : ""}`}>
            {currentLang.code.toUpperCase()}
          </span>
          <ChevronDown className={`h-3 w-3 ${isDoorsPage ? "text-white" : ""}`} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="z-50 w-36 bg-background shadow-md border border-border/50">
        {languages.map((language) => (
          <DropdownMenuItem
            key={language.code}
            onClick={() => setLanguage(language.code)}
            className={`flex items-center space-x-2 cursor-pointer ${
              currentLanguage === language.code ? 'bg-muted text-primary' : ''
            }`}
          >
            <span>{language.flag}</span>
            <span className="text-sm">{language.name}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}